import React from "react";
import { Link } from "react-router-dom";
import { Award, TrendingUp, Phone, Zap, Check } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PrimaryButton } from "@/components/kit";

const BENEFITS = [
  { icon: Award, title: "Featured placement", body: "Your clinic rises to the top of local search results." },
  { icon: TrendingUp, title: "Real analytics", body: "Track profile views, calls, and directions each month." },
  { icon: Phone, title: "More inbound calls", body: "Pet parents can call or get directions in one tap." },
  { icon: Zap, title: "Emergency priority", body: "Premium clinics get priority in the 24/7 filter." },
];

const TIERS = [
  { name: "Basic Featured", price: 49, perks: ["Featured badge", "Higher placement", "Call analytics"] },
  { name: "Premium Featured", price: 149, perks: ["Everything in Basic", "Hero spotlight", "Emergency priority", "Onboarding support"] },
];

export default function ForVets() {
  return (
    <div className="min-h-screen bg-[#FCFBF8] text-[#2D2B2A]">
      <header className="max-w-5xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Logo size={38} to="/" />
        <Link to="/founding-partner"><PrimaryButton className="!py-2.5">Become a partner</PrimaryButton></Link>
      </header>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-8 pb-14 text-center">
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E8F0E5] text-[#4b6b4e] text-xs font-extrabold uppercase tracking-wide">
          For veterinary clinics
        </span>
        <h1 className="font-display text-4xl sm:text-6xl font-black leading-tight mt-5 max-w-3xl mx-auto">
          Reach more pet parents on Wooflect.
        </h1>
        <p className="text-lg text-[#4A4746] mt-5 max-w-2xl mx-auto">
          Get discovered by dog owners searching for trusted local care — with featured placement, analytics, and one-tap calls.
        </p>
        <Link to="/founding-partner" className="inline-block mt-8"><PrimaryButton className="!px-8 !py-3.5 text-base">Create a clinic account</PrimaryButton></Link>
      </section>

      <section className="bg-white border-y border-[#E8E3DA]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((b) => (
            <div key={b.title} className="p-6 rounded-3xl bg-[#FCFBF8] border border-[#E8E3DA]">
              <div className="h-12 w-12 rounded-2xl bg-[#F6DED5] flex items-center justify-center mb-4">
                <b.icon size={24} color="#E86A45" />
              </div>
              <h3 className="font-display font-extrabold text-lg">{b.title}</h3>
              <p className="text-[#4A4746] mt-1.5 text-sm">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <h2 className="font-display text-3xl font-extrabold text-center mb-8">Simple monthly pricing</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {TIERS.map((t, i) => (
            <div key={t.name} className={`p-6 rounded-3xl bg-white border ${i === 1 ? "border-[#E86A45] border-2" : "border-[#E8E3DA]"}`}>
              <p className="font-display text-lg font-extrabold">{t.name}</p>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="font-display text-4xl font-black text-[#E86A45]">${t.price}</span>
                <span className="text-[#4A4746]">/month</span>
              </div>
              <div className="space-y-2 mt-4">
                {t.perks.map((p) => (
                  <div key={p} className="flex items-center gap-2 text-sm"><Check size={16} color="#6F8F72" /> {p}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-[#4A4746] mt-6">
          Bundle with our sister app <b>Purrflect</b> to reach cat owners too. Manage everything after you sign up.
        </p>
        <div className="text-center mt-6">
          <Link to="/founding-partner"><PrimaryButton className="!px-8 !py-3.5 text-base">Get started</PrimaryButton></Link>
        </div>
      </section>

      <footer className="border-t border-[#E8E3DA] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#4A4746]">
          <span>Questions? <a href="mailto:BigDog@happybigdogs.net" className="font-bold text-[#E86A45]">BigDog@happybigdogs.net</a></span>
          <span>© 2026 Happy Big Dogs, LLC</span>
        </div>
      </footer>
    </div>
  );
}
